import { EnvironmentProviders, provideAppInitializer } from '@angular/core';
import { setDmLabels } from '@digitale-menschen/component-library';

/**
 * Registers the German source labels for every `@digitale-menschen/component-library` family
 * that ships English defaults. A family left unset renders English, so every key of every
 * family is listed here — including the ones no current screen reaches.
 *
 * `{count}`, `{page}`, `{current}`, `{total}` and `{query}` are tokens the library substitutes;
 * they have to survive translation verbatim.
 */
export function provideDmLabels(): EnvironmentProviders {
  return provideAppInitializer(() =>
    setDmLabels({
      // `searchPlaceholder` is left out on purpose: set globally it would beat every field's own
      // `placeholder` in its panel search, so a filter named «Standorte» would search «Suche».
      combobox: {
        placeholder: $localize`:@@common.search.placeholder:Suche`,
        selectedSuffix: $localize`:@@common.selected:ausgewählt`,
        resetLabel: $localize`:@@common.reset:Zurücksetzen`,
        selectAllLabel: $localize`:@@common.select-all:Alle auswählen`,
        applyLabel: $localize`:@@common.anwenden:Anwenden`,
        closeLabel: $localize`:@@common.schliessen:Schliessen`,
        stagedStatusLabel: $localize`:@@common.combobox.stagedStatus:{count} ausgewählt, nicht angewendet`,
        createLabel: $localize`:@@common.combobox.erstellen:«{query}» erstellen`,
        loadingLabel: $localize`:@@common.loading:Wird geladen…`,
        emptyStateTitle: $localize`:@@common.combobox.noMatches:Keine Treffer`,
        emptyStateDescription: $localize`:@@common.combobox.noMatchesDescription:Bitte versuchen Sie einen anderen Suchbegriff.`,
        allSelectedTitle: $localize`:@@common.combobox.allSelected:Alles ausgewählt`,
        allSelectedDescription: $localize`:@@common.combobox.allSelectedDescription:Sie haben bereits alle Einträge gewählt.`,
        clearLabel: $localize`:@@common.combobox.auswahlLoeschen:Auswahl löschen`,
        // `searchPlaceholder` stays unset on purpose: unset, each panel's search field reads its own
        // German `placeholder`, whereas a global value would replace that on every field.
      },
      // Accessible names of the state regions only; what the panels show is slotted per table.
      datatable: {
        emptyStateAriaLabel: $localize`:@@common.datatable.keineErgebnisse:Keine Ergebnisse`,
        errorStateAriaLabel: $localize`:@@common.datatable.ladefehler:Fehler beim Laden`,
        offlineStateAriaLabel: $localize`:@@common.datatable.offline:Keine Verbindung`,
      },
      datePicker: {
        placeholderLabel: $localize`:@@common.datum.datumWaehlen:Datum wählen`,
        cancelLabel: $localize`:@@common.cancel:Abbrechen`,
        applyLabel: $localize`:@@common.anwenden:Anwenden`,
        // Its own id despite the identical German: `@@common.loeschen` is the row's delete action.
        clearLabel: $localize`:@@common.datum.leeren:Löschen`,
        pickerAriaLabel: $localize`:@@common.datum.datumsauswahl:Datumsauswahl`,
        calendarAriaLabel: $localize`:@@common.datum.kalender:Kalender`,
        prevMonthLabel: $localize`:@@common.datum.vorherigerMonat:Vorheriger Monat`,
        nextMonthLabel: $localize`:@@common.datum.naechsterMonat:Nächster Monat`,
        todayButtonLabel: $localize`:@@common.datum.heute:Heute`,
        calendarDateInputLabel: $localize`:@@common.datum.datum:Datum`,
        dateInputPlaceholder: $localize`:@@common.datum.datumWaehlen:Datum wählen`,
        monthSelectLabel: $localize`:@@common.datum.monat:Monat`,
        yearSelectLabel: $localize`:@@common.datum.jahr:Jahr`,
        rangePlaceholderLabel: $localize`:@@common.datum.zeitraumWaehlen:Zeitraum wählen`,
        rangePickerAriaLabel: $localize`:@@common.datum.zeitraumauswahl:Zeitraumauswahl`,
        rangeStartLabel: $localize`:@@common.datum.startdatum:Startdatum`,
        rangeEndLabel: $localize`:@@common.datum.enddatum:Enddatum`,
        presetToday: $localize`:@@common.datum.heute:Heute`,
        presetYesterday: $localize`:@@common.datum.gestern:Gestern`,
        presetThisWeek: $localize`:@@common.datum.dieseWoche:Diese Woche`,
        presetLastWeek: $localize`:@@common.datum.letzteWoche:Letzte Woche`,
        presetThisMonth: $localize`:@@common.datum.dieserMonat:Dieser Monat`,
        presetLastMonth: $localize`:@@common.datum.letzterMonat:Letzter Monat`,
        presetThisYear: $localize`:@@common.datum.diesesJahr:Dieses Jahr`,
        presetLastYear: $localize`:@@common.datum.letztesJahr:Letztes Jahr`,
        presetAllTime: $localize`:@@common.datum.gesamterZeitraum:Gesamter Zeitraum`,
        datePresetToday: $localize`:@@common.datum.heute:Heute`,
        datePresetTomorrow: $localize`:@@common.datum.morgen:Morgen`,
        datePresetEndOfWeek: $localize`:@@common.datum.endeDieserWoche:Ende dieser Woche`,
        datePresetInAWeek: $localize`:@@common.datum.inEinerWoche:In einer Woche`,
        datePresetEndOfMonth: $localize`:@@common.datum.endeDesMonats:Ende des Monats`,
      },
      drawer: {
        closeLabel: $localize`:@@common.schliessen:Schliessen`,
      },
      fileupload: {
        uploadtext: $localize`:@@common.fileupload.hochladen:Zum Hochladen klicken`,
        dragdroptext: $localize`:@@common.fileupload.dragDrop:oder per Drag & Drop`,
        invalidtypelabel: $localize`:@@common.fileupload.ungueltigerTyp:Dateityp nicht zulässig`,
        invalidsizlabel: $localize`:@@common.fileupload.dateiZuGross:Datei zu gross`,
        uploadinglabel: $localize`:@@common.fileupload.wirdHochgeladen:Wird hochgeladen…`,
        completelabel: $localize`:@@common.fileupload.abgeschlossen:Abgeschlossen`,
        failedlabel: $localize`:@@common.fileupload.fehlgeschlagen:Fehlgeschlagen`,
        failedmessage: $localize`:@@common.fileupload.fehlgeschlagenBeschreibung:Upload fehlgeschlagen, bitte erneut versuchen`,
        tryagainlabel: $localize`:@@common.erneutVersuchen:Erneut versuchen`,
        // The file name is appended to this by the library, so it reads as a prefix.
        deletelabel: $localize`:@@common.fileupload.dateiLoeschen:Datei löschen`,
      },
      filterBar: {
        label: $localize`:@@common.filter:Filter`,
        filterLabel: $localize`:@@common.filter:Filter`,
        drawerLabel: $localize`:@@common.filter:Filter`,
        // The library appends the hidden-control count to the *default* fold-more label and uses
        // an override as-is — so «Weitere Filter (3)» only works through this global route.
        foldMoreLabel: $localize`:@@compliance.filter.moreFilters:Weitere Filter`,
        foldLessLabel: $localize`:@@compliance.filter.fewerFilters:Weniger Filter`,
      },
      input: {
        showPasswordLabel: $localize`:@@common.input.passwortAnzeigen:Passwort anzeigen`,
        hidePasswordLabel: $localize`:@@common.input.passwortVerbergen:Passwort verbergen`,
        clearLabel: $localize`:@@common.input.eingabeLoeschen:Eingabe löschen`,
      },
      inputDate: {
        ariaLabel: $localize`:@@common.datum.datum:Datum`,
        daySegmentLabel: $localize`:@@common.datum.tag:Tag`,
        monthSegmentLabel: $localize`:@@common.datum.monat:Monat`,
        yearSegmentLabel: $localize`:@@common.datum.jahr:Jahr`,
        // Format hints rather than words, so they are translated as formats.
        daySegmentPlaceholder: $localize`:@@common.datum.platzhalterTag:TT`,
        monthSegmentPlaceholder: $localize`:@@common.datum.platzhalterMonat:MM`,
        yearSegmentPlaceholder: $localize`:@@common.datum.platzhalterJahr:JJJJ`,
      },
      // What a spinner without a caption of its own announces.
      loadingIndicator: {
        label: $localize`:@@common.loading:Wird geladen…`,
      },
      modal: {
        closeLabel: $localize`:@@common.schliessen:Schliessen`,
      },
      overflowList: {
        counterLabel: $localize`:@@common.overflowList.weitereAnzahl:{count} weitere`,
      },
      pagination: {
        prevLabel: $localize`:@@common.pagination.previous:Zurück`,
        nextLabel: $localize`:@@common.pagination.next:Weiter`,
        pageAriaLabel: $localize`:@@common.pagination.pageAriaLabel:Seite {page}`,
        pageStatusLabel: $localize`:@@common.pagination.pageStatus:Seite {current} von {total}`,
      },
      richtextEditor: {
        fontFamily: $localize`:@@richtext.schriftart:Schriftart`,
        fontSize: $localize`:@@richtext.schriftgroesse:Schriftgrösse`,
        bold: $localize`:@@richtext.fett:Fett (Strg+B)`,
        italic: $localize`:@@richtext.kursiv:Kursiv (Strg+I)`,
        underline: $localize`:@@richtext.unterstrichen:Unterstrichen (Strg+U)`,
        heading1: $localize`:@@richtext.ueberschrift1:Überschrift 1`,
        heading2: $localize`:@@richtext.ueberschrift2:Überschrift 2`,
        heading3: $localize`:@@richtext.ueberschrift3:Überschrift 3`,
        textColor: $localize`:@@richtext.textfarbe:Textfarbe`,
        custom: $localize`:@@richtext.benutzerdefiniert:Benutzerdefiniert`,
        alignLeft: $localize`:@@richtext.linksbuendig:Linksbündig`,
        alignCenter: $localize`:@@richtext.zentriert:Zentriert`,
        alignRight: $localize`:@@richtext.rechtsbuendig:Rechtsbündig`,
        bulletList: $localize`:@@richtext.aufzaehlung:Aufzählung`,
        orderedList: $localize`:@@richtext.nummerierteListe:Nummerierte Liste`,
        insertLink: $localize`:@@richtext.linkEinfuegen:Link einfügen`,
        linkUrl: $localize`:@@richtext.linkUrl:URL`,
        linkLabel: $localize`:@@richtext.linkBezeichnung:Bezeichnung`,
        linkOptional: $localize`:@@richtext.optional:(optional)`,
        linkUrlPlaceholder: $localize`:@@richtext.linkUrlPlatzhalter:https://example.com`,
        linkLabelPlaceholder: $localize`:@@richtext.linktext:Linktext`,
        cancel: $localize`:@@common.cancel:Abbrechen`,
        insert: $localize`:@@richtext.einfuegen:Einfügen`,
        linkPreviewEdit: $localize`:@@common.bearbeiten:Bearbeiten`,
        strikethrough: $localize`:@@richtext.durchgestrichen:Durchgestrichen`,
        superscript: $localize`:@@richtext.hochgestellt:Hochgestellt`,
        subscript: $localize`:@@richtext.tiefgestellt:Tiefgestellt`,
        inlineCode: $localize`:@@richtext.inlineCode:Inline-Code`,
        codeBlock: $localize`:@@richtext.codeblock:Codeblock`,
        blockquote: $localize`:@@richtext.zitat:Zitat`,
        indent: $localize`:@@richtext.einzugVergroessern:Einzug vergrössern`,
        outdent: $localize`:@@richtext.einzugVerkleinern:Einzug verkleinern`,
        horizontalRule: $localize`:@@richtext.trennlinie:Trennlinie`,
        clearFormatting: $localize`:@@richtext.formatierungEntfernen:Formatierung entfernen`,
        undo: $localize`:@@richtext.rueckgaengig:Rückgängig`,
        redo: $localize`:@@richtext.wiederholen:Wiederholen`,
        editorAriaLabel: $localize`:@@richtext.inhalt:Rich-Text-Inhalt`,
        toolbarAriaLabel: $localize`:@@richtext.formatierung:Formatierung`,
        // The fallback for an editor without a placeholder of its own; most pass a field-specific one.
        placeholder: $localize`:@@richtext.platzhalter:Text eingeben…`,
        characters: (count: number) => $localize`:@@richtext.zeichen:${count}:count: Zeichen`,
        // The English default clamps at zero before formatting; an override has to do the same
        // or an over-long value counts down past it.
        charactersLeft: (count: number) => $localize`:@@richtext.zeichenVerbleibend:Noch ${Math.max(0, count)}:count: Zeichen`,
      },
      sidebar: {
        sidebarLabel: $localize`:@@common.sidebar.seitenleiste:Seitenleiste`,
        groupActionLabel: $localize`:@@common.sidebar.gruppenaktion:Gruppenaktion`,
        menuActionLabel: $localize`:@@common.sidebar.mehr:Mehr`,
        toggleSidebarLabel: $localize`:@@common.sidebar.umschalten:Seitenleiste ein- oder ausblenden`,
      },
      tabs: {
        moreTriggerLabel: $localize`:@@common.tabs.weitereAnzahl:{count} weitere Tabs`,
        moreMenuLabel: $localize`:@@common.tabs.weitere:Weitere Tabs`,
      },
      toaster: {
        label: $localize`:@@common.toaster.benachrichtigungen:Benachrichtigungen`,
      },
    }),
  );
}
